import '.az{min-height:100dvh;}.ssr-plugin'
import '.ag{box-sizing:border-box;}.ssr-plugin'
import '.cs{padding:96px 96px;}.ssr-plugin'
import '.f{flex:1;}.ssr-plugin'
import '.ak{margin:0 auto;}.ssr-plugin'
import '.o{display:flex;}.ssr-plugin'
import '.am{justify-content:space-between;}.ssr-plugin'
import '.b0{max-width:1280px;}.ssr-plugin'
import '.a{width:100%;}.ssr-plugin'
import '.b1{gap:144px;}.ssr-plugin'
import '.ca{text-align:left;}.ssr-plugin'
import '.ct{min-width:280px;}.ssr-plugin'
import '.bf{max-width:568px;}.ssr-plugin'
import '.bd{gap:24px;}.ssr-plugin'
import '.cu{margin:48px 0px;}.ssr-plugin'
import '.bc{border-radius:40px;}.ssr-plugin'
import '.cv{padding:48px 0px;}.ssr-plugin'
import '.p{flex-direction:column;}.ssr-plugin'
import '.cw{gap:48px;}.ssr-plugin'
import '.cb{gap:12px;}.ssr-plugin'
import '.cx{margin:0 48px;}.ssr-plugin'
import '.bn{opacity:0.5;}.ssr-plugin'
import '.cd{font-size:16px;}.ssr-plugin'
import '.cy{margin:0 32px;}.ssr-plugin'
import '.cz{padding:0 48px;}.ssr-plugin'
import '.d0{gap:32px;}.ssr-plugin'
import '.d1{text-align:justify;}.ssr-plugin'
import '.cr{margin-bottom:24px;}.ssr-plugin'
import '.d2{margin-bottom:24px;}.ssr-plugin'
import '.d3{flex-direction:row;}.ssr-plugin'
import '.d4{margin-top:1px;}.ssr-plugin'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { styled } from 'inlines'
import { colors } from '../../utils/colors.js'
import { Button } from '../../components/Button.js'
import { IconButton } from '../../components/IconButton.js'
import { Icon } from '../../assets/icons/index.js'
import { Logo } from '../../assets/svg/Logo.js'
import { TextInput } from '../../components/TextInput.js'
import { CheckboxInput } from '../../components/CheckboxInput.js'
import { useLocation } from 'wouter'
import {
  Payment,
  PaymentElement,
  usePayment,
} from '@based/payment-plugin/browser'
import { useQuery } from '@based'
import { Turnstile, type TurnstileHandle } from './Turnstile.js'
import { useVotingRegion, VotingRegion } from '../../hooks/useVotingRegion.js'
import { useEmail } from '../../hooks/useEmail.js'
import { isEmailValid } from '../../../../shared/emailValidation.js'
import { ProcessingPaymentPortal } from './ProcessingPaymentPortal.js'
import { TABLET, MOBILE } from '../../utils/breakpoints.js'
import { transparentColor } from '../../utils/transparentcolor.js'
import { useLanguage } from '../../hooks/useLanguage.js'
import { useDictionary } from '../../hooks/useDictionary.js'
import { useScrollToTop } from '../../hooks/useScrollToTop.js'
import { ErrorUserMessages } from '../../../../shared/errors.js'
import { checkEligibleCardGeo } from '../../../../shared/voting.js'
import { Meter } from '../../components/Meter.js'
import { Layout } from '../../components/Layout.js'
import { EmailErrorNote, ErrorNote } from './Errors.js'
import { type ParticipatingVotingRegionsResponse } from '../../../../functions/audience/participating-voting-regions/index.js'
import { useTrack } from '../../hooks/useTrack.js'
import { AnalyticsEvent } from '../../../../shared/analyticsEvents.js'

type CheckoutPageProps = { votes?: Record<string, number> }

export const CheckoutPage = ({ votes }: CheckoutPageProps) => {
  const track = useTrack('checkout')
  useEffect(() => {
    track(AnalyticsEvent.PAGE_LOAD)
  }, [])

  const [, setLocation] = useLocation()
  const [votingRegion, setVotingRegion] = useVotingRegion()

  useScrollToTop()

  const totalVotes = Object.values(votes ?? {}).reduce((a, b) => a + b, 0) // SAFE: empty object when votes not yet loaded

  if (!totalVotes) {
    setLocation('/')
  }

  const amount = (votingRegion.price ?? 0) * totalVotes // SAFE: zero price when data not yet loaded
  const currency = votingRegion.currency || 'eur' // SAFE: fallback to EUR when currency not yet loaded
  const dictionary = useDictionary()

  const { data: publishableKeys, loading: loadingPublishableKeys } = useQuery(
    'payment-stripe-pubkey',
  )

  const {
    data: participatingVotingRegions,
    loading: loadingParticipatingVotingRegions,
  } = useQuery('participating-voting-regions')

  return loadingPublishableKeys || loadingParticipatingVotingRegions ? (
    <Layout>
      <Meter
        value={0}
        simulated
        style={{
          width: '100%',
          maxWidth: '160px',
          marginBottom: '24px',
        }}
      />
    </Layout>
  ) : (
    <div className="az ag"
      id="checkout-page-content"
    >
      {!totalVotes ? (
        0
      ) : (
        <Payment
          stripePublishableKey={publishableKeys.stripePublishableKey}
          locale="en"
          amount={amount}
          currency={currency}
          metadata={{
            votes,
            voteGeo: votingRegion.code,
          }}
          appearance={{
            variables: {
              colorPrimary: '#fff',
              colorBackground: 'rgb(1, 10, 65)',
              colorText: 'rgb(255, 255, 255, 0.5)',
              colorDanger: '#df1b41',
              fontFamily: 'Neulis-Regular, Helvetica, system-ui, sans-serif',
              fontWeightNormal: '400',
              fontSizeBase: '16px',
              borderRadius: '40px',
            },
            rules: {
              '.AccordionItem': {
                border: 'none',
              },
              '.Input': {
                borderColor: 'rgba(255,255,255,0.24)',
                backgroundColor: 'rgba(1, 10, 65, 0)',
                color: 'white',
              },
              '.Input::placeholder': {
                color: 'rgba(255,255,255,0.5)',
              },
              '.Label': {
                marginBottom: '8px',
              },
            },
          }}
          postConfirmationTokenCheck={async ({ setErrorMessage, cardGeo }) => {
            // INFO: this check happens in the frontend before
            // process payment is requested
            if (!cardGeo || typeof votingRegion.name !== 'string') {
              setErrorMessage(
                `${ErrorUserMessages.CantConfirmCardCountry}::0101`,
              )
              return false
            }

            if (!participatingVotingRegions) {
              setErrorMessage(
                `${ErrorUserMessages.CantConfirmCardCountry}::0102`,
              )
              return false
            }

            try {
              checkEligibleCardGeo(
                participatingVotingRegions,
                votingRegion,
                cardGeo,
              )
            } catch (_error) {
              setErrorMessage(
                `${ErrorUserMessages.CardNotEligible}::${cardGeo}::0103`,
              )
              return false
            }
            return true
          }}
        >
          <Content
            votes={votes as any}
            votingRegion={votingRegion}
            setVotingRegion={setVotingRegion}
            currency={currency}
            amount={amount}
            dictionary={dictionary}
            participatingVotingRegions={participatingVotingRegions!}
          />
        </Payment>
      )}
    </div>
  )
}

export const Content = ({
  votes,
  votingRegion,
  setVotingRegion,
  currency,
  amount,
  dictionary,
  participatingVotingRegions,
}: {
  votes: Record<string, number>
  votingRegion: VotingRegion
  setVotingRegion: (code: string) => void
  currency: string
  amount: number
  dictionary: Record<string, string>
  participatingVotingRegions: ParticipatingVotingRegionsResponse
}) => {
  const [location, setLocation] = useLocation()
  const [lang] = useLanguage()

  const votesCastedForString = Object.keys(votes).join('-')

  const {
    complete,
    submitPayment,
    isWaiting,
    isDelayed,
    isProcessing,
    success,
    error,
    setError,
  } = usePayment()
  const turnstileRef = useRef<TurnstileHandle>(null)

  if (success) {
    setLocation(`/thank-you/${votesCastedForString}`)
  }

  if ((!error && isWaiting) || isDelayed || isProcessing) {
    setLocation(location + '?loading=true')
  }
  if (error) {
    setLocation(location.replaceAll('?loading=true', ''))
  }

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [email] = useEmail()
  const [agreedTerms, setAgreedTerms] = useState(false)
  const [agreedNews, setAgreedNews] = useState(false)

  useEffect(() => {
    if (error === null) {
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    }
  }, [error])

  const enabled =
    isEmailValid(email) &&
    !email.endsWith('.') &&
    complete &&
    !error &&
    agreedTerms &&
    !!turnstileToken

  // TODO: remove after new sdk
  type RequestPaymentFunctionPayload = {
    captchaToken: string | null
    metadata: {
      voteGeo: string
      votes: { [countryCode: string]: number }
    }
  }
  const requestPaymentFunctionPayload: RequestPaymentFunctionPayload = {
    captchaToken: turnstileToken,
    metadata: {
      votes,
      voteGeo: votingRegion.code, // TODO: rename voteGeo to voteRegion everywhere
    },
  }

  const handlePayButton = () => {
    // if (!payButtonClickedTimer.current) {
    //   payButtonClickedTimer.current = setTimeout(() => {
    //     payButtonClickedTimer.current = null
    //   }, 500)
    return submitPayment({
      email,
      requestPaymentFunctionPayload,
    })
    // }
  }

  return (
    <styled.div className="cs f"
      style={{
        [TABLET]: {
          padding: '48px 48px',
        },
        [MOBILE]: {
          padding: '16px 16px',
        },
      }}
    >
      <styled.div className="ak o am b0 a b1 ca"
        style={{
          '& h2': { textAlign: lang.rtl ? 'right' : 'left' },
          '& h3': { textAlign: lang.rtl ? 'right' : 'left' },
          '& label': { textAlign: lang.rtl ? 'right' : 'left' },
          '& #turnstile-container': { textAlign: lang.rtl ? 'right' : 'left' },
          [TABLET]: {
            flexDirection: 'column',
            gap: '16px',
          },
        }}
      >
        <styled.div className="ct bf a f"
          style={{
            '& p': {
              opacity: 0.5,
              fontSize: 16,
              display: 'flex',
              gap: 12,
            },
            '& svg': {
              minWidth: 24,
            },
          }}
        >
          <div className="o bd">
            <IconButton
              icon={<Icon type="backIcon" />}
              onClick={() => setLocation('/cart')}
            />
            <Logo width={106} height={36} onClick={() => setLocation('/')} />
          </div>
          <styled.h2 className="cu"
            style={{
              [TABLET]: {
                marginBottom: '24px',
              },
            }}
          >
            {dictionary.checkout}
          </styled.h2>
          <AboutTransactionsBlock
            style={{ [TABLET]: { display: 'none' } }}
            dictionary={dictionary}
          />
        </styled.div>

        {/* Right Column */}
        <styled.div className="blur bf a bc cv"
          style={{
            backgroundColor: transparentColor(colors.secondary, 0.5),
            border: `1px solid ${colors.border}`,
            '& p': {
              fontSize: 16,
            },
            '& a': {
              textDecoration: 'underline',
            },
            [TABLET]: {
              maxWidth: '100%',
            },
            [MOBILE]: {
              maxWidth: '100%',
              padding: '24px 0px',
              paddingTop: 32,
            },
          }}
        >
          <PaymentForm
            enabled={enabled}
            isWaiting={isWaiting}
            isProcessing={isProcessing}
            isDelayed={isDelayed}
            setTurnstileToken={setTurnstileToken}
            agreedTerms={agreedTerms}
            setAgreedTerms={setAgreedTerms}
            agreedNews={agreedNews}
            setAgreedNews={setAgreedNews}
            handlePayButton={handlePayButton}
            currency={currency}
            amount={amount}
            error={error}
            setError={setError}
            turnstileRef={turnstileRef}
            dictionary={dictionary}
            participatingVotingRegions={participatingVotingRegions}
            votingRegion={votingRegion}
            setVotingRegion={setVotingRegion}
          />
        </styled.div>
      </styled.div>
      {(!error && isWaiting) || isDelayed || isProcessing ? (
        <ProcessingPaymentPortal />
      ) : null}
    </styled.div>
  )
}

const PaymentForm = ({
  enabled,
  isWaiting,
  isProcessing,
  isDelayed,
  setTurnstileToken,
  agreedTerms,
  setAgreedTerms,
  agreedNews,
  setAgreedNews,
  handlePayButton,
  currency,
  amount,
  error,
  setError,
  turnstileRef,
  dictionary,
  participatingVotingRegions,
  votingRegion,
  setVotingRegion,
}: {
  enabled: boolean
  isWaiting: boolean
  isDelayed: boolean
  isProcessing: boolean
  setTurnstileToken: React.Dispatch<React.SetStateAction<string | null>>
  agreedTerms: boolean
  setAgreedTerms: React.Dispatch<React.SetStateAction<boolean>>
  agreedNews: boolean
  setAgreedNews: React.Dispatch<React.SetStateAction<boolean>>
  handlePayButton: () => void
  currency: string
  amount: number
  error: string | null
  setError: Dispatch<SetStateAction<string | null>>
  turnstileRef: React.RefObject<TurnstileHandle | null>
  dictionary: Record<string, string>
  participatingVotingRegions: ParticipatingVotingRegionsResponse
  votingRegion: VotingRegion
  setVotingRegion: (code: string) => void
}) => {
  return (
    <div className="o p cw"
    >
      <SelectPaymentMethod dictionary={dictionary} />
      <Contact dictionary={dictionary} error={error} setError={setError} />
      <AboutTransactionsBlock
        dictionary={dictionary}
        style={{
          display: 'none',
          [TABLET]: {
            display: 'block',
            padding: '0px 48px',
            '& svg': {
              opacity: 0.5,
              marginBottom: '-6px',
              marginRight: '6px',
            },
          },
          [MOBILE]: { padding: '0px 23px' },
        }}
      />
      <Terms
        dictionary={dictionary}
        agreedTerms={agreedTerms}
        setAgreedTerms={setAgreedTerms}
        agreedNews={agreedNews}
        setAgreedNews={setAgreedNews}
      />
      <TurnstileWidget
        setTurnstileToken={setTurnstileToken}
        turnstileRef={turnstileRef}
      />
      {error && (
        <ErrorNote
          error={error}
          dictionary={dictionary}
          participatingVotingRegions={participatingVotingRegions}
          votingRegion={votingRegion}
          setVotingRegion={setVotingRegion}
        />
      )}
      <PayButton
        dictionary={dictionary}
        enabled={enabled}
        isWaiting={isWaiting}
        isProcessing={isProcessing}
        isDelayed={isDelayed}
        currency={currency}
        amount={amount}
        handlePayButton={handlePayButton}
      />
    </div>
  )
}

const SelectPaymentMethod = ({
  dictionary,
}: {
  dictionary: Record<string, string>
}) => {
  return (
    <div className="o p cb"
    >
      <styled.h3 className="cx"
        style={{
          [MOBILE]: {
            margin: '0px 24px',
          },
        }}
      >
        {dictionary.selectPaymentMethod}
      </styled.h3>
      <styled.p className="cx bn cd"
        style={{
          [MOBILE]: {
            margin: '0px 24px',
          },
        }}
      >
        {dictionary.allTransactionsSecured}
      </styled.p>
      <styled.div className="cy"
        style={{
          [MOBILE]: {
            margin: '0px 8px',
          },
        }}
      >
        <PaymentElement />
      </styled.div>
    </div>
  )
}

const Contact = ({
  dictionary,
  error,
  setError,
}: {
  dictionary: Record<string, string>

  error: string | null
  setError: Dispatch<SetStateAction<string | null>>
}) => {
  const [email, setEmail] = useEmail()

  return (
    <styled.div className="o p cz d0"
      style={{
        [MOBILE]: {
          padding: '0px 24px',
        },
      }}
    >
      <styled.h3>{dictionary.contact}</styled.h3>
      <TextInput
        value={email}
        onChange={(e) => {
          if (error) {
            setError(null)
          }
          setEmail(e.target.value)
        }}
        onBlur={(e) => {
          if (!isEmailValid(email)) {
            setError(() => ErrorUserMessages.InvalidEmail)
          }
        }}
        label={dictionary.emailFieldLabel}
        style={{ width: 'calc(100% - 24px)' }}
        placeholder="yourname@mail.com"
        id="checkout-page-email-input"
      />
      {error && <EmailErrorNote dictionary={dictionary} error={error} />}
    </styled.div>
  )
}

const Terms = ({
  agreedTerms,
  setAgreedTerms,
  agreedNews,
  setAgreedNews,
  dictionary,
}: {
  agreedTerms: boolean
  setAgreedTerms: React.Dispatch<React.SetStateAction<boolean>>
  agreedNews: boolean
  setAgreedNews: React.Dispatch<React.SetStateAction<boolean>>
  dictionary: Record<string, string>
}) => {
  return (
    <styled.div className="o p cz d0"
      style={{
        [MOBILE]: {
          padding: '0 24px',
        },
      }}
    >
      <styled.h3>{dictionary.termsOfService}</styled.h3>
      <CheckboxInput
        id="checkout-page-action-accept-terms"
        value={agreedTerms}
        onChange={(e) => setAgreedTerms(e.target.checked)}
        label={(() => {
          const [before, after] = (dictionary.termsCheckbox ?? '').split(
            '{{TERMS}}',
          )
          return (
            <p>
              {before}
              <a href="/terms-of-service" target="_blank">
                {dictionary.termsOfService}
              </a>
              {after}
            </p>
          )
        })()}
      />
    </styled.div>
  )
}

const TurnstileWidget = ({
  setTurnstileToken,
  turnstileRef,
}: {
  setTurnstileToken: React.Dispatch<React.SetStateAction<string | null>>
  turnstileRef: React.RefObject<TurnstileHandle | null>
}) => {
  return (
    <styled.div className="o p cz d1 d0"
      style={{
        [MOBILE]: {
          padding: '0px 16px',
        },
      }}
    >
      <Turnstile ref={turnstileRef} setTurnstileToken={setTurnstileToken} />
    </styled.div>
  )
}

const PayButton = ({
  enabled,
  isWaiting,
  isProcessing,
  isDelayed,
  handlePayButton,
  currency,
  amount,
  dictionary,
}: {
  enabled: boolean
  isWaiting: boolean
  isDelayed: boolean
  isProcessing: boolean
  handlePayButton: () => void
  currency: string
  amount: number
  dictionary: Record<string, string>
}) => {
  return (
    <styled.div className="o p cz cb"
      style={{
        [MOBILE]: {
          padding: '0px 24px',
        },
      }}
    >
      <Button
        disabled={!enabled || isWaiting || isDelayed || isProcessing}
        onClick={handlePayButton}
        id="checkout-page-pay-button"
      >
        {dictionary.payButton}
      </Button>
    </styled.div>
  )
}

const AboutTransactionsBlock = ({ style, dictionary }) => {
  return (
    <styled.div style={style}>
      <h3 className="cr">
        {dictionary.disclaimers}
      </h3>

      <styled.div className="d2 o d3 cb"
        style={{
          [TABLET]: {
            marginBottom: '16px',
          },}}
      >
        <Icon
          type="infoIcon"
          style={{ opacity: 0.5, minWidth: 24, minHeight: 24 }}
        />
        <p className="d4">
          {dictionary.noRefundNotice}
        </p>
      </styled.div>
      <styled.div className="d2 o d3 cb"
        style={{
          [TABLET]: {
            marginBottom: '16px',
          },}}
      >
        <Icon
          type="infoIcon"
          style={{ opacity: 0.5, minWidth: 24, minHeight: 24 }}
        />
        <p className="d4">
          {dictionary.extraFeeNotice}
        </p>
      </styled.div>
      <styled.div className="d2 o d3 cb"
        style={{
          [TABLET]: {
            marginBottom: '16px',
          },}}
      >
        <Icon
          type="infoIcon"
          style={{ opacity: 0.5, minWidth: 24, minHeight: 24 }}
        />
        <p className="d4">
          {dictionary.taxesIncludedNotice}
        </p>
      </styled.div>
    </styled.div>
  )
}
